<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import BackgroundRemovalPreview from '@/components/Tools/BackgroundRemovalPreview.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { triggerBlobDownload } from '@/composables/useBlobDownload';
import { useImageUploadLimit } from '@/composables/useImageUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import {
    isWebAssemblySupported,
    removeImageBackground,
} from '@/lib/backgroundRemovalClient';
import { dashboard } from '@/routes';
import { removeBackground } from '@/routes/tools/image';

const BG_ACCEPT = 'image/jpeg,image/png,image/webp';

const { lineUpTo, maxMb } = useImageUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.image.remove_background.seo',
    fallbackTitleKey: 'tools.image.remove_background.title',
    fallbackTitleLabel: 'Remove background',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: removeBackground.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.image.remove_background.title',
                title: 'Remove background',
                href: removeBackground.url(),
            },
        ],
    },
});

type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const originalObjectUrl = ref<string | null>(null);
const resultObjectUrl = ref<string | null>(null);
const resultBlob = ref<Blob | null>(null);
const status = ref<Status>('idle');
const statusMessage = ref('');
const progressPercent = ref<number | null>(null);

const canProcess = computed(
    () => file.value !== null && isWebAssemblySupported(),
);

const downloadFilename = computed(() => {
    if (!file.value) {
        return 'no-background.png';
    }

    const base = file.value.name.replace(/\.[^.]+$/, '') || 'image';

    return `${base}-no-background.png`;
});

function revokeUrls(): void {
    if (originalObjectUrl.value) {
        URL.revokeObjectURL(originalObjectUrl.value);
        originalObjectUrl.value = null;
    }

    if (resultObjectUrl.value) {
        URL.revokeObjectURL(resultObjectUrl.value);
        resultObjectUrl.value = null;
    }
}

watch(file, (f) => {
    revokeUrls();
    resultBlob.value = null;
    progressPercent.value = null;

    if (f) {
        originalObjectUrl.value = URL.createObjectURL(f);
    }
});

onUnmounted(() => {
    revokeUrls();
});

function onFileSelected(files: File[]): void {
    const selected = files[0] ?? null;

    if (!selected) {
        file.value = null;
        status.value = 'idle';
        statusMessage.value = '';

        return;
    }

    if (selected.size > maxMb.value * 1024 * 1024) {
        status.value = 'error';
        statusMessage.value = t(
            'tools.image.ui.bg_removal_file_too_large',
            'This file exceeds the upload limit. Try a smaller image for browser processing.',
        ).replace(':max', String(maxMb.value));

        return;
    }

    file.value = selected;
    status.value = 'idle';
    statusMessage.value = '';
    resultBlob.value = null;
}

function onRemoveFile(): void {
    file.value = null;
    status.value = 'idle';
    statusMessage.value = '';
    resultBlob.value = null;
    progressPercent.value = null;
}

function reset(): void {
    onRemoveFile();
}

async function runRemoval(): Promise<void> {
    if (!file.value || status.value === 'loading') {
        return;
    }

    if (!isWebAssemblySupported()) {
        status.value = 'error';
        statusMessage.value = t(
            'tools.image.ui.bg_removal_wasm_unsupported',
            'Your browser does not support WebAssembly, which is required for background removal. Try a newer browser.',
        );

        return;
    }

    status.value = 'loading';
    progressPercent.value = 0;
    statusMessage.value = t(
        'tools.image.ui.bg_removal_loading_model',
        'Loading AI model…',
    );

    try {
        const blob = await removeImageBackground(file.value, {
            onProgress: ({ phase, current, total }) => {
                if (total > 0) {
                    progressPercent.value = Math.round((current / total) * 100);
                }

                statusMessage.value =
                    phase === 'model'
                        ? t(
                              'tools.image.ui.bg_removal_loading_model',
                              'Loading AI model…',
                          )
                        : t(
                              'tools.image.ui.bg_removal_processing',
                              'Removing background…',
                          );
            },
        });

        if (resultObjectUrl.value) {
            URL.revokeObjectURL(resultObjectUrl.value);
        }

        resultBlob.value = blob;
        resultObjectUrl.value = URL.createObjectURL(blob);
        progressPercent.value = null;
        status.value = 'success';
        statusMessage.value = t(
            'tools.image.ui.bg_removal_success',
            'Background removed. Download your transparent PNG or compare below.',
        );
    } catch {
        progressPercent.value = null;
        status.value = 'error';
        statusMessage.value = t(
            'tools.image.ui.bg_removal_failed',
            'Background removal failed. Try a smaller image or a different browser.',
        );
    }
}

function downloadResult(): void {
    if (!resultBlob.value) {
        return;
    }

    triggerBlobDownload(resultBlob.value, downloadFilename.value);
}
</script>

<template>
    <ToolPageHead
        v-bind="seo"
        json-ld-head-key="ld-json-image-remove-background"
    />

    <ToolLayout
        :title="t('tools.image.remove_background.title', '')"
        :subtitle="t('tools.image.remove_background.description', '')"
        client-side-processing
        client-side-notice-key="tools.data_retention_notice_client_generic"
        how-to-placement="below_preview"
        preview-track="wide"
    >
        <template #preview>
            <div
                class="flex min-h-[min(50vh,22rem)] items-center justify-center overflow-hidden rounded-xl border border-border bg-muted/20 p-3 shadow-inner"
            >
                <BackgroundRemovalPreview
                    v-if="originalObjectUrl && resultObjectUrl"
                    class="w-full max-w-full"
                    :before-src="originalObjectUrl"
                    :after-src="resultObjectUrl"
                />
                <img
                    v-else-if="originalObjectUrl && file"
                    :src="originalObjectUrl"
                    :alt="file.name"
                    class="max-h-[min(50vh,22rem)] w-full object-contain"
                />
                <p
                    v-else
                    class="px-4 text-center text-sm text-muted-foreground"
                >
                    {{
                        t(
                            'tools.image.ui.bg_removal_preview_empty',
                            'Upload a photo to preview and remove its background.',
                        )
                    }}
                </p>
            </div>
        </template>

        <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <DropZone
                    :accept="BG_ACCEPT"
                    :label="t('tools.image.ui.drop_label', '')"
                    :sublabel="
                        lineUpTo(
                            t(
                                'tools.image.ui.bg_removal_accept',
                                'JPEG, PNG, or WebP',
                            ),
                        )
                    "
                    :preview-file="file"
                    @update:model-value="onFileSelected"
                />
                <div v-if="file" class="mt-3">
                    <FileCard :file="file" @remove="onRemoveFile" />
                </div>
            </div>

            <Button
                type="button"
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="!canProcess || status === 'loading'"
                @click="runRemoval"
            >
                <span v-if="status === 'loading'">
                    {{
                        progressPercent !== null && progressPercent > 0
                            ? `${t('tools.image.ui.bg_removal_processing', 'Removing background…')} ${progressPercent}%`
                            : t(
                                  'tools.image.ui.bg_removal_loading_model',
                                  'Loading AI model…',
                              )
                    }}
                </span>
                <span v-else>{{
                    t('tools.image.ui.bg_removal_submit', 'Remove background')
                }}</span>
            </Button>

            <Button
                v-if="resultBlob"
                type="button"
                variant="outline"
                class="w-full"
                @click="downloadResult"
            >
                {{
                    t(
                        'tools.image.ui.bg_removal_download',
                        'Download transparent PNG',
                    )
                }}
            </Button>

            <StatusMessage :status="status" :message="statusMessage" />

            <Button
                v-if="resultBlob"
                type="button"
                variant="ghost"
                class="w-full"
                @click="reset"
            >
                {{ t('tools.image.ui.process_another', 'Process another') }}
            </Button>
        </div>
    </ToolLayout>
</template>
